import React from "react";
import { ArrowRight, CircleAlert, FileSpreadsheet } from "lucide-react";
import { compactPrice } from "@/helpers/pastClientHomes";
import { skippedReasonLines } from "@/helpers/importSummary";
import "./CsvColumnPreview.scss";

export const FIELD_LABELS = {
  type: "Buyer or seller",
  firstName: "First name",
  lastName: "Last name",
  email: "Email",
  phone: "Phone",
  minPrice: "Min price",
  maxPrice: "Max price",
  beds: "Beds",
  baths: "Baths",
  cities: "Cities",
  zips: "ZIP codes",
  propertyTypes: "Property type",
  timeline: "Timeline",
  street: "Street address",
  city: "City",
  state: "State",
  zip: "ZIP",
  price: "Price",
};

const CONTACT_FIELDS = ["firstName", "lastName", "email", "phone"];

const priceRange = (min, max) => {
  if (min && max) return `${compactPrice(min)} to ${compactPrice(max)}`;
  if (max) return `Up to ${compactPrice(max)}`;
  if (min) return `From ${compactPrice(min)}`;
  return null;
};

export const sampleLine = (item) => {
  if (item.type === "buyer" && item.buyer) {
    const b = item.buyer;
    const place =
      b.cities?.map((c) => [c.city, c.state].filter(Boolean).join(", "))[0] ||
      (b.zips?.length ? `ZIP ${b.zips.slice(0, 3).join(", ")}` : null);
    return [
      place,
      priceRange(b.minPrice, b.maxPrice),
      b.beds ? `${b.beds}+ beds` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  if (item.seller) {
    const s = item.seller;
    return [
      s.street,
      [s.city, s.state, s.zip].filter(Boolean).join(" "),
      s.price ? compactPrice(s.price) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return "";
};

const plural = (n, one, many) =>
  `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

const CsvColumnPreview = ({ fileName, preview, onChangeFile }) => {
  const { columns, counts, sample, skipped } = preview;
  const mapped = new Set(columns.map((c) => c.field).filter(Boolean));
  const missingType = !mapped.has("type");
  const missingContact = !CONTACT_FIELDS.some((f) => mapped.has(f));

  return (
    <div className="csv-column-preview">
      <div className="ccp-file">
        <FileSpreadsheet size={20} strokeWidth={1.75} aria-hidden />
        <span className="ccp-file__name">{fileName}</span>
        <span className="ccp-file__rows">
          {plural(counts.rows, "row", "rows")}
        </span>
        <button type="button" className="ccp-link" onClick={onChangeFile}>
          Choose a different file
        </button>
      </div>

      <dl className="ccp-counts">
        <div>
          <dt>{counts.buyers.toLocaleString("en-US")}</dt>
          <dd>
            {counts.buyers === 1
              ? "buyer becomes a draft Buyer Want-Listing"
              : "buyers become draft Buyer Want-Listings"}
          </dd>
        </div>
        <div>
          <dt>{counts.sellers.toLocaleString("en-US")}</dt>
          <dd>
            {counts.sellers === 1 ? "seller becomes a prospect property" : "sellers become prospect properties"}
          </dd>
        </div>
        {counts.clients ? (
          <div>
            <dt>{counts.clients.toLocaleString("en-US")}</dt>
            <dd>{counts.clients === 1 ? "contact is" : "contacts are"} added to My Clients only</dd>
          </div>
        ) : null}
        <div className={counts.skipped ? "ccp-counts--warn" : ""}>
          <dt>{counts.skipped.toLocaleString("en-US")}</dt>
          <dd>{counts.skipped === 1 ? "row can't" : "rows can't"} be imported</dd>
        </div>
      </dl>

      {(missingType || missingContact) && (
        <div className="ccp-warning" role="alert">
          <CircleAlert size={16} aria-hidden />
          {missingType
            ? "Add a type column that says buyer or seller on each row."
            : "Add a name, email or phone column so we can match each client."}
        </div>
      )}

      <div className="ccp-section">
        <div className="ccp-section__title">How we read your columns</div>
        <ul className="ccp-columns">
          {columns.map((column, idx) => (
            <li
              key={`${column.header}-${idx}`}
              className={column.field ? "" : "ccp-columns__unused"}
            >
              <span className="ccp-columns__header">{column.header}</span>
              <ArrowRight size={14} aria-hidden />
              <span className="ccp-columns__field">
                {column.field ? FIELD_LABELS[column.field] : "Not used"}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {sample.length > 0 && (
        <div className="ccp-section">
          <div className="ccp-section__title">
            The first rows, as we'll import them
          </div>
          <ul className="ccp-sample">
            {sample.map((item) => (
              <li key={`${item.row}-${item.type}`}>
                <span className="ccp-sample__type">
                  {item.type === "buyer" ? "Buyer" : "Seller"}
                </span>
                <span className="ccp-sample__name">
                  {item.name || "No name"}
                </span>
                <span className="ccp-sample__line">{sampleLine(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="ccp-section">
          <div className="ccp-section__title">Rows we'll skip</div>
          <ul className="ccp-skipped">
            {skippedReasonLines(skipped, 5).map((line, idx) => (
              <li key={`${line}-${idx}`}>{line}</li>
            ))}
          </ul>
          {skipped.length > 5 && (
            <p className="ccp-more">
              And {plural(skipped.length - 5, "more row", "more rows")}. Fix
              them in your file and upload it again any&nbsp;time.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CsvColumnPreview;
